import React, { useMemo, useEffect, useRef, useState } from 'react';
import { ArrowLeft, TrendingUp, TrendingDown, DollarSign, PieChart, Activity, Calendar, Pencil, Trash2 } from 'lucide-react';
import { PerformanceChart } from './Charts';
import { convertCurrency, getCurrencySymbol } from '../utils/currency';
import { Asset, AssetOverrides, Security, Transaction } from '../types';
import { fetchTimeframeSeriesByProviderIdNoMassive, MarketQuote, MarketTimeframe } from '../services/marketDataService';
import { fetchCachedQuote } from '../services/quoteCacheService';
import { resolveProviderId } from '../services/dividendService';
import { getFxRate } from '../services/fxService';
import { log } from '../utils/logger';
import { normalizeMoneyUnit } from '../utils/quoteNormalization';
import { getAssetNativeCurrency } from '../utils/holdingDisplay';
import { StockAdvicePanel } from './StockAdvicePanel';

interface AssetDetailProps {
    asset: Asset;
    onBack: () => void;
    onSaveOverride?: (symbol: string, patch: AssetOverrides) => void;
    startEditing?: boolean;
    uid?: string | null;
    // Optional callback so the parent table can be updated when we fetch a fresh quote in the detail view.
    onQuoteUpdate?: (quoteKey: string, quote: MarketQuote, options?: { providerKey?: string; symbol?: string; instrumentId?: string }) => void;
    transactions?: Transaction[];
    onUpdateTransaction?: (transaction: Transaction) => void;
    onDeleteTransaction?: (transactionId: string) => void;
}

type Timeframe = MarketTimeframe;

const formatLabel = (d: Date, timeframe: Timeframe) => {
    if (timeframe === '1D') {
        return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }
    if (timeframe === '1Y') {
        return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
    }
    if (timeframe === 'All') {
        return d.toLocaleDateString(undefined, { year: 'numeric' });
    }
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
};

const generateFallbackSeries = (timeframe: Timeframe, displayPrice: number) => {
    let points = 30;
    let volatility = 0.05;

    switch (timeframe) {
        case '1D':
            points = 24;
            volatility = 0.01;
            break;
        case '1W':
            points = 7;
            volatility = 0.03;
            break;
        case '1M':
            points = 30;
            volatility = 0.05;
            break;
        case '1Y':
            points = 12;
            volatility = 0.15;
            break;
        case 'All':
            points = 60;
            volatility = 0.25;
            break;
    }

    const now = new Date();
    const data: { name: string; value: number }[] = [];
    let price = displayPrice;

    for (let i = 0; i < points; i++) {
        const idxFromEnd = points - 1 - i;

        const d = new Date(now);
        if (timeframe === '1D') d.setHours(now.getHours() - idxFromEnd);
        else if (timeframe === '1W') d.setDate(now.getDate() - idxFromEnd);
        else if (timeframe === '1M') d.setDate(now.getDate() - idxFromEnd);
        else if (timeframe === '1Y') d.setMonth(now.getMonth() - idxFromEnd);
        else d.setMonth(now.getMonth() - idxFromEnd);

        const change = price * volatility * (Math.random() - 0.48);
        price = Math.max(0.0001, price - change);

        data.push({ name: formatLabel(d, timeframe), value: price });
    }

    return data;
};

const symbolFor = (currency: string) => {
    const value = (currency || '').toUpperCase();
    if (value === 'GBX') return 'p';
    return getCurrencySymbol(value || 'GBP');
};

const AssetDetail: React.FC<AssetDetailProps> = ({
    asset,
    onBack,
    onSaveOverride,
    startEditing,
    uid,
    onQuoteUpdate,
    transactions = [],
    onUpdateTransaction,
    onDeleteTransaction,
}) => {
    const [timeframe, setTimeframe] = useState<Timeframe>('1M');
    const [chartData, setChartData] = useState<{ name: string; value: number }[]>([]);
    const [livePrice, setLivePrice] = useState<number | null>(null);
    const [approxBasePrice, setApproxBasePrice] = useState<number | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editValues, setEditValues] = useState({
        quantity: '',
        avgPrice: '',
        firstBuyDate: '',
        notes: '',
    });
    const [editTx, setEditTx] = useState<Transaction | null>(null);
    const [showAdvicePanel, setShowAdvicePanel] = useState(false);
    const [editTxForm, setEditTxForm] = useState({
        date: '',
        action: 'Buy' as Transaction['action'],
        quantity: '',
        price: '',
        priceCurrency: '',
        amount: '',
        currency: '',
    });
    const debugUntilRef = useRef(Date.now() + 10_000);

    const toDateInput = (value?: string) => {
        if (!value) return '';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '';
        return d.toISOString().slice(0, 10);
    };

    const sourceLabelFor = (tx: Transaction) => {
        if (tx.sourceLabel?.trim()) return tx.sourceLabel.trim();
        if (tx.source === 'manual') return 'Added manually';
        if (tx.source === 'csv') return 'Imported from CSV';
        if (tx.source === 'auto') return 'Added automatically';
        if (tx.source === 'snapshot') return 'Imported snapshot';
        return 'Imported / legacy entry';
    };

    const formatTransactionDate = (value?: string) => {
        if (!value) return 'Unknown date';
        const isoDateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})/)?.slice(1).map(Number);
        const d = isoDateOnly
            ? new Date(isoDateOnly[0], isoDateOnly[1] - 1, isoDateOnly[2])
            : new Date(value);
        if (Number.isNaN(d.getTime())) return 'Unknown date';
        return d.toLocaleDateString();
    };

    const baseCurrency = asset.baseCurrency || 'GBP';
    const nativeCurrency = getAssetNativeCurrency(asset);
    const quoteCurrency = nativeCurrency;
    const baseSymbol = symbolFor(baseCurrency);
    const quoteSymbol = symbolFor(quoteCurrency);

    const nativePrice = useMemo(() => {
        const candidate = livePrice ?? asset.currentPriceNative ?? asset.currentPrice ?? 0;
        return Number.isFinite(candidate) ? candidate : 0;
    }, [livePrice, asset.currentPriceNative, asset.currentPrice]);

    const basePrice = useMemo(() => {
        if (approxBasePrice !== null && Number.isFinite(approxBasePrice)) return approxBasePrice;
        if (Number.isFinite(asset.currentPrice)) return asset.currentPrice;
        return nativePrice * (asset.quoteScaleToBase ?? 1);
    }, [approxBasePrice, asset.currentPrice, nativePrice, asset.quoteScaleToBase]);

    // Timeframe return (from chart series)
    const timeframeReturnPct = useMemo(() => {
        if (!chartData || chartData.length < 2) return 0;
        const first = chartData[0]?.value ?? 0;
        const last = chartData[chartData.length - 1]?.value ?? 0;
        if (first <= 0) return 0;
        return ((last - first) / first) * 100;
    }, [chartData]);

    const isPositiveTf = timeframeReturnPct >= 0;

    const totalDividends = asset.totalDividends || 0;

    const assetTransactions = useMemo(() => {
        const symbolKey = (asset.symbol || '').trim().toUpperCase();
        return transactions
            .filter((tx) => {
                const txSymbol = String(tx.symbol || '').trim().toUpperCase();
                return txSymbol === symbolKey && !tx.deletedAt;
            })
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }, [asset.symbol, transactions]);

    const netQuantityFromTransactions = useMemo(() => {
        let runningQuantity = 0;

        assetTransactions.forEach((tx) => {
            const action = String(tx.action || '').trim().toLowerCase();
            const quantity = Number(tx.quantity) || 0;

            if (action.includes('buy')) {
                runningQuantity += quantity;
                return;
            }

            if (action.includes('sell')) {
                runningQuantity = Math.max(0, runningQuantity - quantity);
            }
        });

        return runningQuantity;
    }, [assetTransactions]);

    const quantityForDisplay = useMemo(() => {
        const hasTradeTransactions = assetTransactions.some((tx) => {
            const action = String(tx.action || '').trim().toLowerCase();
            return action.includes('buy') || action.includes('sell');
        });

        return hasTradeTransactions ? netQuantityFromTransactions : asset.quantity;
    }, [asset.quantity, assetTransactions, netQuantityFromTransactions]);

    const avgPriceForDisplay = useMemo(() => {
        const nativeAvg = Number(asset.avgPriceNative);
        if (Number.isFinite(nativeAvg) && nativeAvg > 0) return nativeAvg;

        const fallbackAvg = Number(asset.avgPrice);
        if (!Number.isFinite(fallbackAvg) || fallbackAvg <= 0) return 0;

        if (baseCurrency.toUpperCase() === quoteCurrency.toUpperCase()) return fallbackAvg;

        const converted = convertCurrency(fallbackAvg, baseCurrency, quoteCurrency);
        return Number.isFinite(converted) && converted > 0 ? converted : fallbackAvg;
    }, [asset.avgPrice, asset.avgPriceNative, baseCurrency, quoteCurrency]);

    const currentValue = quantityForDisplay * nativePrice;
    const explicitForwardAnnualDividend = Number(
        asset.forwardAnnualDividend ?? asset.annualDividend ?? asset.annualDividendEstimate ?? 0
    );
    const providerYield = Number(asset.forwardDividendYield ?? asset.dividendYield ?? 0);
    const yieldFraction = Number.isFinite(providerYield) && providerYield > 0
        ? providerYield > 1 ? providerYield / 100 : providerYield
        : 0;
    const derivedForwardAnnualDividend = explicitForwardAnnualDividend > 0
        ? explicitForwardAnnualDividend
        : currentValue > 0 && yieldFraction > 0
            ? currentValue * yieldFraction
            : 0;
    const forwardAnnualDividend = Number.isFinite(derivedForwardAnnualDividend) ? derivedForwardAnnualDividend : 0;
    const sinceBuyPct = avgPriceForDisplay > 0 ? ((nativePrice - avgPriceForDisplay) / avgPriceForDisplay) * 100 : 0;

    const isPositiveSinceBuy = sinceBuyPct >= 0;

    const heldSince = useMemo(() => {
        if (!asset.firstBuyDate) return null;
        const d = new Date(asset.firstBuyDate);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    }, [asset.firstBuyDate]);

    const normalizeTransactionUnit = (value: number, currency: string | undefined, tx: Transaction) => {
        const normalized = normalizeMoneyUnit({
            value,
            rawCurrency: currency || tx.originalPriceCurrency || tx.priceCurrency || quoteCurrency,
            exchange: tx.exchange || asset.exchange,
            symbol: tx.symbol || asset.symbol,
        });
        return normalized;
    };

    const unitPriceInQuote = (tx: Transaction) => {
        const unitPrice = Number(tx.originalPricePerShare ?? tx.pricePerShare ?? tx.price) || 0;
        const normalized = normalizeTransactionUnit(unitPrice, tx.originalPriceCurrency || tx.priceCurrency, tx);
        const txCurrency = normalized.currency.toUpperCase();
        const quote = quoteCurrency.toUpperCase();

        if (txCurrency === quote) return normalized.value;

        const convertedDirect = convertCurrency(normalized.value, txCurrency, quote);
        if (Number.isFinite(convertedDirect) && convertedDirect > 0) return convertedDirect;

        return normalized.value;
    };


    const normalizeLiveQuoteForAsset = (quote: MarketQuote): MarketQuote => quote;

    const dividendQuantityMismatch = useMemo(() => {
        const maxDividendQuantity = assetTransactions.reduce((max, tx) => {
            const action = String(tx.action || '').trim().toLowerCase();
            if (!action.includes('dividend')) return max;
            return Math.max(max, Number(tx.quantity || tx.shares) || 0);
        }, 0);
        return quantityForDisplay > 0 && maxDividendQuantity > quantityForDisplay * 2;
    }, [assetTransactions, quantityForDisplay]);

    const formatDividendSummary = (tx: Transaction) => {
        const cash = Math.abs(Number(tx.total ?? tx.amount) || 0);
        const cashCurrency = tx.totalCurrency || tx.currency || baseCurrency;
        const quantity = Number(tx.quantity || tx.shares) || 0;
        const rawPerShare = Number(tx.originalPricePerShare ?? tx.pricePerShare ?? tx.price) || 0;
        const perShare = normalizeTransactionUnit(rawPerShare, tx.originalPriceCurrency || tx.priceCurrency, tx);
        return { cash, cashCurrency, quantity, perShare };
    };

    const getPriceMovement = (tx: Transaction) => {
        const txPriceQuote = unitPriceInQuote(tx);
        if (!Number.isFinite(txPriceQuote) || txPriceQuote <= 0) return null;
        const quantity = Math.abs(Number(tx.quantity) || 0);
        if (quantity <= 0) return null;

        const perShareMove = nativePrice - txPriceQuote;
        const action = String(tx.action || '').toLowerCase();
        const direction = action.includes('sell') ? -1 : 1;
        const pnl = perShareMove * quantity * direction;
        const pct = (perShareMove / txPriceQuote) * 100 * direction;
        return { pnl, pct };
    };

    const openTxEditor = (tx: Transaction) => {
        const unitPrice = Number(tx.pricePerShare ?? tx.originalPricePerShare ?? tx.price) || 0;
        setEditTx(tx);
        setEditTxForm({
            date: toDateInput(tx.date),
            action: tx.action,
            quantity: String(Number(tx.quantity) || 0),
            price: String(unitPrice),
            priceCurrency: tx.priceCurrency || quoteCurrency,
            amount: String(Number(tx.amount) || 0),
            currency: tx.currency || baseCurrency,
        });
    };

    const handleSaveTransactionEdit = () => {
        if (!editTx || !onUpdateTransaction) return;
        const nextPrice = Number(editTxForm.price) || 0;
        const nextTx: Transaction = {
            ...editTx,
            date: editTxForm.date || editTx.date,
            action: editTxForm.action,
            quantity: Number(editTxForm.quantity) || 0,
            price: nextPrice,
            pricePerShare: nextPrice,
            originalPricePerShare: nextPrice,
            priceCurrency: (editTxForm.priceCurrency || editTx.priceCurrency || quoteCurrency).toUpperCase(),
            amount: Number(editTxForm.amount) || 0,
            currency: (editTxForm.currency || editTx.currency || baseCurrency).toUpperCase(),
        };
        onUpdateTransaction(nextTx);
        setEditTx(null);
    };

    useEffect(() => {
        const firstBuyDate = asset.firstBuyDate ? new Date(asset.firstBuyDate) : null;
        const formattedDate = firstBuyDate && !Number.isNaN(firstBuyDate.getTime()) ? firstBuyDate.toISOString().slice(0, 10) : '';
        setEditValues({
            quantity: Number.isFinite(asset.quantity) ? asset.quantity.toString() : '',
            avgPrice: Number.isFinite(asset.avgPrice) ? asset.avgPrice.toString() : '',
            firstBuyDate: formattedDate,
            notes: asset.overrides?.notes || '',
        });
    }, [asset]);

    useEffect(() => {
        if (startEditing) {
            setIsEditing(true);
        }
    }, [startEditing]);

    useEffect(() => {
        let cancelled = false;

        const convertToBase = async () => {
            if (!Number.isFinite(nativePrice)) {
                if (!cancelled) setApproxBasePrice(null);
                return;
            }

            const scaled = nativePrice * (asset.quoteScaleToBase ?? 1);
            if (quoteCurrency.toUpperCase() === baseCurrency.toUpperCase()) {
                if (!cancelled) setApproxBasePrice(scaled);
                return;
            }

            const fx = await getFxRate(quoteCurrency, baseCurrency);
            if (!cancelled) setApproxBasePrice(scaled * fx);
        };

        convertToBase();
        return () => {
            cancelled = true;
        };
    }, [nativePrice, quoteCurrency, baseCurrency, asset.quoteScaleToBase]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                // Build a Security object for Polygon resolution (supports UK .L normalization via dividendService)
                const security: Security = {
                    id: asset.id || asset.symbol,
                    symbol: asset.symbol,
                    name: asset.name,
                    isin: asset.isin,
                    exchange: asset.exchange,
                    dataProviderId: asset.dataProviderId,
                    currency: quoteCurrency,
                };

                if (Date.now() < debugUntilRef.current) {
                    log('MarketData request for:', {
                        assetSymbol: asset.symbol,
                        assetId: asset.id,
                        isin: asset.isin,
                        exchange: asset.exchange,
                        dataProviderId: asset.dataProviderId,
                    });
                }

                const providerId = await resolveProviderId(security, { uid });
                if (Date.now() < debugUntilRef.current) {
                    log('Resolved providerId:', providerId, 'for symbol:', security.symbol);
                }

                let series: { date: Date; close: number }[] = [];
                let latestQuote: MarketQuote | null = null;
                let labelTimeframe: Timeframe = timeframe;
                if (providerId) {
                    latestQuote = await fetchCachedQuote(providerId, { force: true });
                    if (latestQuote) {
                        latestQuote = normalizeLiveQuoteForAsset(latestQuote);
                        onQuoteUpdate?.((latestQuote as any).canonicalQuoteKey || latestQuote.providerId || providerId, latestQuote, {
                            providerKey: (latestQuote as any).canonicalQuoteKey || asset.canonicalQuoteKey || (asset.dataProviderId ? `eodhd:${asset.dataProviderId}` : undefined),
                            symbol: asset.symbol,
                            instrumentId: asset.instrumentId,
                        });
                    }
                    if (!cancelled && latestQuote?.price) {
                        setLivePrice(latestQuote.price);
                    }
                }

                if (providerId) {
                    series = await fetchTimeframeSeriesByProviderIdNoMassive(providerId, timeframe, { uid, security });
                    if (timeframe === '1D' && series.length < 2) {
                        const fallback = await fetchTimeframeSeriesByProviderIdNoMassive(providerId, '1W', { uid, security });
                        if (fallback.length > 1) {
                            series = fallback;
                            labelTimeframe = '1W';
                        }
                    }
                }

                // Keep current/header price driven by persisted quotes-table data only.
                // Historical series must not backfill or override the latest quote display.
                const latestNativeValue: number | null = latestQuote?.price ?? null;

                if (!cancelled && latestNativeValue !== null) {
                    setLivePrice(latestNativeValue);
                }

                if (!cancelled && series.length > 1) {
                    const mapped = series.map((p) => {
                        return {
                            name: formatLabel(p.date, labelTimeframe),
                            value: p.close,
                        };
                    });

                    setChartData(mapped);
                    return;
                }

                // 3) Fallback to mock series with *real date labels*
                if (!cancelled) {
                    const fallbackPrice = latestNativeValue ?? asset.currentPriceNative ?? asset.currentPrice ?? 0;
                    setChartData(generateFallbackSeries(timeframe, fallbackPrice));
                }
            } catch (error) {
                if (!cancelled) {
                    const fallbackPrice = asset.currentPriceNative ?? asset.currentPrice ?? 0;
                    setChartData(generateFallbackSeries(timeframe, fallbackPrice));
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asset.symbol, asset.isin, asset.exchange, asset.dataProviderId, asset.currency, asset.priceCurrency, timeframe, quoteCurrency, asset.currentPrice, asset.currentPriceNative]);

    const handleSave = () => {
        if (!onSaveOverride) {
            setIsEditing(false);
            return;
        }

        const quantity = Number(editValues.quantity);
        const avgPrice = Number(editValues.avgPrice);
        const patch: AssetOverrides = {};

        if (Number.isFinite(quantity)) patch.quantity = quantity;
        if (Number.isFinite(avgPrice)) patch.avgPrice = avgPrice;
        patch.firstBuyDate = editValues.firstBuyDate ? new Date(editValues.firstBuyDate).toISOString() : undefined;
        patch.notes = editValues.notes.trim() || undefined;

        onSaveOverride(asset.symbol, patch);
        setIsEditing(false);
    };

    return (
        <div className="bg-[#1e1e26] rounded-2xl border border-white/5 shadow-xl overflow-hidden animate-in fade-in slide-in-from-right-4 duration-300">
            {/* Header */}
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={onBack}
                        className="p-2 hover:bg-white/5 rounded-lg text-slate-400 hover:text-white transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </button>

                    <div>
                        <div className="flex items-center space-x-3">
                            <h2 className="text-xl font-bold text-white">{asset.name}</h2>
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-slate-800 text-[#cbd5e1] border border-white/5">
                                {asset.symbol}
                            </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mt-1">
                            <div className="flex flex-col">
                                <span className="text-2xl font-bold text-white">
                                    {quoteSymbol}
                                    {nativePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>

                                {quoteCurrency.toUpperCase() !== baseCurrency.toUpperCase() && approxBasePrice !== null && (
                                    <div className="text-xs text-slate-500 mt-1">
                                        ≈ {baseSymbol}
                                        {approxBasePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} in
                                        your base currency
                                    </div>
                                )}
                            </div>

                            {/* Timeframe return */}
                            <span
                                className={`text-sm font-medium px-2 py-0.5 rounded flex items-center ${
                                    isPositiveTf ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
                                }`}
                                title="Return over selected timeframe"
                            >
                                {isPositiveTf ? (
                                    <TrendingUp size={12} className="mr-1" />
                                ) : (
                                    <TrendingDown size={12} className="mr-1" />
                                )}
                                {timeframe}: {timeframeReturnPct >= 0 ? '+' : ''}
                                {timeframeReturnPct.toFixed(2)}%
                            </span>

                            {/* Since-buy return */}
                            <span
                                className={`text-sm font-medium px-2 py-0.5 rounded flex items-center ${
                                    isPositiveSinceBuy ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
                                }`}
                                title={heldSince ? `Since first buy (${heldSince})` : 'Since average cost'}
                            >
                                {isPositiveSinceBuy ? (
                                    <TrendingUp size={12} className="mr-1" />
                                ) : (
                                    <TrendingDown size={12} className="mr-1" />
                                )}
                                Since buy: {sinceBuyPct >= 0 ? '+' : ''}
                                {sinceBuyPct.toFixed(2)}%
                            </span>
                        </div>

                        {heldSince && <div className="mt-1 text-xs text-slate-500">Held since {heldSince}</div>}
                    </div>
                </div>

                <div className="hidden sm:flex items-center space-x-2">
                    {asset.type === 'Stock' && (
                        <button
                            onClick={() => setShowAdvicePanel((prev) => !prev)}
                            className="flex items-center space-x-2 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-300 px-3 py-1.5 rounded-lg text-xs font-medium border border-emerald-500/30 transition-colors"
                        >
                            <span>AI Advice</span>
                        </button>
                    )}
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center space-x-2 bg-indigo-600/10 hover:bg-indigo-600/20 text-indigo-300 px-3 py-1.5 rounded-lg text-xs font-medium border border-indigo-500/30 transition-colors"
                    >
                        <Pencil size={12} />
                        <span>Edit holding</span>
                    </button>
                    <div className="flex space-x-1 bg-[#13131a] p-1 rounded-lg border border-white/5">
                        {(['1D', '1W', '1M', '1Y', 'All'] as Timeframe[]).map((tf) => (
                            <button
                                key={tf}
                                onClick={() => setTimeframe(tf)}
                                className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${
                                    timeframe === tf ? 'bg-[#2a2a35] text-white shadow-sm' : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
                                }`}
                            >
                                {tf}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Chart Section */}
            <div className="p-6">
                <div className="mb-6">
                    <PerformanceChart
                        data={chartData}
                        color={isPositiveTf ? '#10b981' : '#f43f5e'}
                        currencySymbol={quoteSymbol}
                    />
                </div>

                {/* Mobile Timeframe Selector */}
                <div className="flex sm:hidden justify-between bg-[#13131a] p-1 rounded-lg border border-white/5 mb-8">
                    {(['1D', '1W', '1M', '1Y', 'All'] as Timeframe[]).map((tf) => (
                        <button
                            key={tf}
                            onClick={() => setTimeframe(tf)}
                            className={`flex-1 px-2 py-2 text-xs font-medium rounded transition-all ${
                                timeframe === tf ? 'bg-[#2a2a35] text-white shadow-sm' : 'text-slate-500'
                            }`}
                        >
                            {tf}
                        </button>
                    ))}
                </div>

                <div className="flex sm:hidden justify-end mb-6">
                    <div className="flex items-center gap-2">
                        {asset.type === 'Stock' && (
                            <button
                                onClick={() => setShowAdvicePanel((prev) => !prev)}
                                className="flex items-center space-x-2 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-300 px-3 py-2 rounded-lg text-xs font-medium border border-emerald-500/30 transition-colors"
                            >
                                <span>AI Advice</span>
                            </button>
                        )}
                        <button
                            onClick={() => setIsEditing(true)}
                            className="flex items-center space-x-2 bg-indigo-600/10 hover:bg-indigo-600/20 text-indigo-300 px-3 py-2 rounded-lg text-xs font-medium border border-indigo-500/30 transition-colors"
                        >
                            <Pencil size={12} />
                            <span>Edit holding</span>
                        </button>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-[#13131a] p-4 rounded-xl border border-white/5">
                        <div className="flex items-center space-x-2 text-slate-500 text-xs mb-1">
                            <DollarSign size={14} />
                            <span>Total Value</span>
                        </div>
                        <div className="text-lg font-bold text-white">{`${quoteSymbol}${currentValue.toFixed(2)}`}</div>
                    </div>

                    <div className="bg-[#13131a] p-4 rounded-xl border border-white/5">
                        <div className="flex items-center space-x-2 text-slate-500 text-xs mb-1">
                            <PieChart size={14} />
                            <span>Quantity</span>
                        </div>
                        <div className="text-lg font-bold text-white">{quantityForDisplay.toLocaleString()}</div>
                    </div>

                    <div className="bg-[#13131a] p-4 rounded-xl border border-white/5">
                        <div className="flex items-center space-x-2 text-slate-500 text-xs mb-1">
                            <Activity size={14} />
                            <span>Avg Price</span>
                        </div>
                        <div className="text-lg font-bold text-white">{`${quoteSymbol}${avgPriceForDisplay.toFixed(3)}`}</div>
                    </div>

                    <div className="bg-[#13131a] p-4 rounded-xl border border-white/5">
                        <div className="flex items-center space-x-2 text-slate-500 text-xs mb-1">
                            <Calendar size={14} />
                            <span>Total Dividends</span>
                        </div>
                        <div className="text-lg font-bold text-emerald-400">{`${baseSymbol}${totalDividends.toFixed(2)}`}</div>
                    </div>

                    <div className="bg-[#13131a] p-4 rounded-xl border border-white/5">
                        <div className="flex items-center space-x-2 text-slate-500 text-xs mb-1">
                            <DollarSign size={14} />
                            <span>Forward Annual Dividend</span>
                        </div>
                        <div className="text-lg font-bold text-slate-200">{`${quoteSymbol}${forwardAnnualDividend.toFixed(2)}`}</div>
                        <div className="text-xs text-slate-500">provider estimate</div>
                    </div>
                </div>

                {asset.type === 'Stock' && <StockAdvicePanel asset={asset} isOpen={showAdvicePanel} />}
            </div>

            <div className="px-6 pb-6">
                <div className="bg-[#13131a] p-4 rounded-xl border border-white/5">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-white">Transactions</h3>
                        <span className="text-xs text-slate-500">{assetTransactions.length} total</span>
                    </div>
                    {assetTransactions.length === 0 ? (
                        <p className="text-xs text-slate-500">No transactions recorded for this holding.</p>
                    ) : (
                        <div className="space-y-2">
                            {dividendQuantityMismatch && (
                                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                                    Dividend history may be incomplete or out of sync with imported buy history. Upload full broker history for accurate historical return calculations.
                                </div>
                            )}
                            {assetTransactions.map((tx) => {
                                const isDividend = String(tx.action || '').trim().toLowerCase().includes('dividend');
                                const movement = isDividend ? null : getPriceMovement(tx);
                                const dividendSummary = isDividend ? formatDividendSummary(tx) : null;
                                const isPositiveMove = (movement?.pnl || 0) >= 0;
                                const actionClass = tx.action === 'Buy' ? 'text-emerald-300' : tx.action === 'Sell' ? 'text-amber-300' : 'text-slate-300';
                                return (
                                    <div key={tx.id} className="rounded-lg border border-white/5 px-3 py-3 text-xs">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="space-y-1">
                                                <div className={`font-semibold ${actionClass}`}>{tx.action}</div>
                                                <div className="text-slate-300">{formatTransactionDate(tx.date)}</div>
                                                {isDividend && dividendSummary ? (
                                                    <div className="space-y-0.5 text-slate-400">
                                                        <div className="text-emerald-300">
                                                            {symbolFor(dividendSummary.cashCurrency)}{dividendSummary.cash.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} imported dividend total
                                                        </div>
                                                        {dividendSummary.quantity > 0 && (
                                                            <div>{dividendSummary.quantity.toLocaleString(undefined, { maximumFractionDigits: 2 })} shares at dividend date</div>
                                                        )}
                                                        {dividendSummary.perShare.value > 0 && (
                                                            <div>
                                                                {symbolFor(dividendSummary.perShare.currency)}{dividendSummary.perShare.value.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })} per share historical dividend received
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="text-slate-400">{Number(tx.quantity).toLocaleString(undefined, { maximumFractionDigits: 4 })} shares @ {symbolFor(tx.priceCurrency || quoteCurrency)}{unitPriceInQuote(tx).toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
                                                )}
                                                <div className="inline-flex items-center rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2 py-0.5 text-[11px] text-indigo-200" title={sourceLabelFor(tx)}>
                                                    {sourceLabelFor(tx)}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                {isDividend && dividendSummary ? (
                                                    <div className="text-[11px] text-emerald-300">Historical dividend received</div>
                                                ) : movement ? (
                                                    <>
                                                        <div className={`font-semibold ${isPositiveMove ? 'text-emerald-400' : 'text-rose-400'}`}>
                                                            {movement.pnl >= 0 ? '+' : ''}{quoteSymbol}{Math.abs(movement.pnl).toFixed(2)}
                                                        </div>
                                                        <div className={`text-[11px] ${isPositiveMove ? 'text-emerald-400/80' : 'text-rose-400/80'}`}>
                                                            {movement.pct >= 0 ? '+' : ''}{movement.pct.toFixed(2)}% since trade
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-[11px] text-slate-500">No price movement data</div>
                                                )}
                                                <div className="mt-2 flex items-center justify-end gap-1">
                                                    {onUpdateTransaction && (
                                                        <button
                                                            onClick={() => openTxEditor(tx)}
                                                            className="text-slate-400 hover:text-indigo-300 p-1 hover:bg-white/5 rounded transition-colors"
                                                            title="Edit transaction"
                                                        >
                                                            <Pencil size={14} />
                                                        </button>
                                                    )}
                                                    {onDeleteTransaction && (
                                                        <button
                                                            onClick={() => onDeleteTransaction(tx.id)}
                                                            className="text-slate-400 hover:text-rose-400 p-1 hover:bg-white/5 rounded transition-colors"
                                                            title="Delete transaction"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {editTx && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
                    <div className="bg-[#1e1e26] border border-white/10 rounded-2xl shadow-2xl w-full max-w-xl">
                        <div className="p-6 border-b border-white/10 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-white">Edit transaction</h3>
                                <p className="text-xs text-slate-500 mt-1">Update this entry directly from transaction history.</p>
                            </div>
                            <button onClick={() => setEditTx(null)} className="text-slate-500 hover:text-white" aria-label="Close transaction modal">
                                ✕
                            </button>
                        </div>
                        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className="text-xs text-slate-400 flex flex-col gap-2">
                                Date
                                <input
                                    type="date"
                                    value={editTxForm.date}
                                    onChange={(event) => setEditTxForm((prev) => ({ ...prev, date: event.target.value }))}
                                    className="bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500/70"
                                />
                            </label>
                            <label className="text-xs text-slate-400 flex flex-col gap-2">
                                Action
                                <select
                                    value={editTxForm.action}
                                    onChange={(event) => setEditTxForm((prev) => ({ ...prev, action: event.target.value as Transaction['action'] }))}
                                    className="bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500/70"
                                >
                                    <option value="Buy">Buy</option>
                                    <option value="Sell">Sell</option>
                                    <option value="Dividend">Dividend</option>
                                    <option value="Deposit">Deposit</option>
                                    <option value="Withdrawal">Withdrawal</option>
                                </select>
                            </label>
                            <label className="text-xs text-slate-400 flex flex-col gap-2">
                                Quantity
                                <input
                                    type="number"
                                    step="0.0001"
                                    value={editTxForm.quantity}
                                    onChange={(event) => setEditTxForm((prev) => ({ ...prev, quantity: event.target.value }))}
                                    className="bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500/70"
                                />
                            </label>
                            <label className="text-xs text-slate-400 flex flex-col gap-2">
                                Price
                                <input
                                    type="number"
                                    step="0.0001"
                                    value={editTxForm.price}
                                    onChange={(event) => setEditTxForm((prev) => ({ ...prev, price: event.target.value }))}
                                    className="bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500/70"
                                />
                            </label>
                            <label className="text-xs text-slate-400 flex flex-col gap-2">
                                Amount
                                <input
                                    type="number"
                                    step="0.0001"
                                    value={editTxForm.amount}
                                    onChange={(event) => setEditTxForm((prev) => ({ ...prev, amount: event.target.value }))}
                                    className="bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500/70"
                                />
                            </label>
                            <label className="text-xs text-slate-400 flex flex-col gap-2">
                                Currency
                                <input
                                    type="text"
                                    value={editTxForm.currency}
                                    onChange={(event) => setEditTxForm((prev) => ({ ...prev, currency: event.target.value.toUpperCase() }))}
                                    className="bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500/70"
                                />
                            </label>
                        </div>
                        <div className="p-6 border-t border-white/10 flex items-center justify-end gap-3">
                            <button onClick={() => setEditTx(null)} className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-lg hover:bg-white/5">
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveTransactionEdit}
                                className="px-4 py-2 text-sm font-medium bg-indigo-500/20 text-indigo-200 rounded-lg border border-indigo-500/40 hover:bg-indigo-500/30"
                            >
                                Save transaction
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {isEditing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
                    <div className="bg-[#1e1e26] border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg">
                        <div className="p-6 border-b border-white/10 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-white">Edit holding</h3>
                                <p className="text-xs text-slate-500 mt-1">Overrides are saved to this portfolio only.</p>
                            </div>
                            <button
                                onClick={() => setIsEditing(false)}
                                className="text-slate-500 hover:text-white"
                                aria-label="Close edit modal"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <label className="text-xs text-slate-400 flex flex-col gap-2">
                                    Quantity (shares)
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        step="0.0001"
                                        value={editValues.quantity}
                                        onChange={(event) => setEditValues((prev) => ({ ...prev, quantity: event.target.value }))}
                                        className="bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500/70"
                                    />
                                </label>
                                <label className="text-xs text-slate-400 flex flex-col gap-2">
                                    Unit / cost price ({quoteSymbol})
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        step="0.0001"
                                        value={editValues.avgPrice}
                                        onChange={(event) => setEditValues((prev) => ({ ...prev, avgPrice: event.target.value }))}
                                        className="bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500/70"
                                    />
                                </label>
                            </div>
                            <label className="text-xs text-slate-400 flex flex-col gap-2">
                                First buy date (optional)
                                <input
                                    type="date"
                                    value={editValues.firstBuyDate}
                                    onChange={(event) => setEditValues((prev) => ({ ...prev, firstBuyDate: event.target.value }))}
                                    className="bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500/70"
                                />
                            </label>
                            <label className="text-xs text-slate-400 flex flex-col gap-2">
                                Notes (optional)
                                <textarea
                                    value={editValues.notes}
                                    onChange={(event) => setEditValues((prev) => ({ ...prev, notes: event.target.value }))}
                                    rows={3}
                                    className="bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500/70 resize-none"
                                />
                            </label>
                        </div>
                        <div className="p-6 border-t border-white/10 flex items-center justify-end gap-3">
                            <button
                                onClick={() => setIsEditing(false)}
                                className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-lg hover:bg-white/5"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-4 py-2 text-sm font-medium bg-indigo-500/20 text-indigo-200 rounded-lg border border-indigo-500/40 hover:bg-indigo-500/30"
                            >
                                Save changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssetDetail;
